import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Users, Lock, CheckCircle2 } from "lucide-react";

/**
 * Public read + write view for a committee section share-link.
 *
 * Department contributors receive a URL like /shared/committee/{token} and
 * can fill in the notes / decision for exactly one section — never see or
 * modify other parts of the meeting.
 */
export default function SharedCommitteeSection() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [decision, setDecision] = useState("");
  const [signer, setSigner] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState("");

  const load = useCallback(async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/shared/committee/${token}`;
      const r = await axios.get(url);
      setData(r.data);
      setNotes(r.data.section.notes || "");
      setDecision(r.data.section.decision || "");
    } catch (e) {
      setError(e?.response?.data?.detail || "Unable to load this share link.");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!signer.trim()) {
      alert("Please enter your name or initials before saving.");
      return;
    }
    setSaving(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/shared/committee/${token}`;
      await axios.put(url, {
        notes,
        decision,
        signed_by: signer.trim(),
      });
      setSavedAt(new Date().toLocaleString());
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex flex-col items-center justify-center gap-2 p-6" data-testid="shared-committee-error">
        <Lock className="w-8 h-8 text-[#b91c1c]" />
        <div className="text-lg font-semibold text-[#0f172a]">Access denied</div>
        <div className="text-sm text-[#475569]">{error}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center text-[#475569]">Loading…</div>;
  }

  const { facility_name, committee_name, scheduled_at, location, section, label } = data;

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="shared-committee-view">
      <header className="border-b border-[#e2e8f0] bg-white px-6 py-5">
        <div className="max-w-4xl mx-auto flex items-start gap-3">
          <svg viewBox="0 0 32 32" className="w-8 h-8 text-[#0284c7] mt-0.5" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M16 28c6.627 0 12-5.373 12-12S22.627 4 16 4 4 9.373 4 16s5.373 12 12 12Z" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 16c0-3.866 3.134-7 7-7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="16" cy="16" r="2.5" fill="currentColor"/>
          </svg>
          <div className="flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#94a3b8]">
              {facility_name || "Committee"} · Shared contribution
            </div>
            <div className="font-heading font-bold text-lg text-[#0f172a] tracking-tight">
              {committee_name}
            </div>
            <div className="text-xs text-[#475569] mt-0.5">
              {scheduled_at?.slice(0, 10)} · {location || "—"}
              {label && <span className="ml-2 px-1.5 py-0.5 rounded bg-[#f0f9ff] text-[#0284c7] text-[10px] font-semibold uppercase tracking-widest">{label}</span>}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-4">
        <div className="rounded-xl border border-[#e2e8f0] bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5 text-[#0284c7]" />
            <div className="font-heading text-xl font-bold text-[#0f172a] tracking-tight">
              {section.title}
            </div>
          </div>

          <label className="block text-[10px] font-semibold uppercase tracking-widest text-[#475569] mb-1">
            Your content / discussion notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={12}
            data-testid="shared-notes"
            className="w-full border border-[#e2e8f0] rounded-md p-3 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-[#0284c7] focus:border-transparent"
            placeholder="Enter your report, indicators, updates, or discussion notes here…"
          />

          <label className="block text-[10px] font-semibold uppercase tracking-widest text-[#475569] mb-1 mt-3">
            Decision (optional)
          </label>
          <input
            value={decision}
            onChange={(e) => setDecision(e.target.value)}
            data-testid="shared-decision"
            className="w-full border border-[#e2e8f0] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0284c7] focus:border-transparent"
            placeholder="e.g. Approved / Deferred / Referred"
          />

          <label className="block text-[10px] font-semibold uppercase tracking-widest text-[#475569] mb-1 mt-3">
            Your name or initials
          </label>
          <input
            value={signer}
            onChange={(e) => setSigner(e.target.value)}
            data-testid="shared-signer"
            className="w-full border border-[#e2e8f0] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0284c7] focus:border-transparent"
            placeholder="e.g. J. Smith, RN"
          />

          <button
            onClick={save}
            disabled={saving}
            data-testid="shared-save"
            className="mt-4 px-4 py-2 rounded-md bg-[#0284c7] hover:bg-[#0369a1] text-white text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {saving ? "Saving…" : "Submit contribution"}
          </button>
          {savedAt && (
            <div className="mt-3 flex items-center gap-1 text-xs text-[#16a34a]">
              <CheckCircle2 className="w-4 h-4" /> Saved · {savedAt}
            </div>
          )}
        </div>

        <div className="text-[10px] text-[#94a3b8] text-center">
          Your contribution is linked to the committee's live agenda. The committee chair will see your entry immediately.
        </div>
      </main>
    </div>
  );
}
